import type { FieldSelection, IdFieldSelection, IdRelationQuery, RelationQuery } from '#layers/data-model/types/field-selection';
import type { GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model/types/schema/ids';
import type { ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import { isFileThumbnailField } from '#layers/data-model/utils/fields/single-relation-field';

import { mergeFieldSelections } from './merge-field-selection';

export interface IdToFieldSelectionContext {
	findPrimitiveFieldById: (id: GlobalPrimitiveFieldId) => ResolvedMonospacePrimitiveField | undefined;
	findSingleRelationFieldById: (id: GlobalSingleRelationFieldId) => ResolvedMonospaceSingleRelationField | undefined;
}

export function idFieldSelectionToFieldSelection(
	selection: IdFieldSelection,
	ctx: IdToFieldSelectionContext,
): FieldSelection {
	const { fields, include } = selection;
	let result: FieldSelection = {};

	if (fields) {
		result.fields = fields.flatMap((id) => {
			if (id === '*') return ['*'];
			const field = ctx.findPrimitiveFieldById(id as GlobalPrimitiveFieldId);
			return field ? [field.apiName] : [];
		});
	}

	for (const [id, query] of Object.entries(include ?? {})) {
		const relation = ctx.findSingleRelationFieldById(id as GlobalSingleRelationFieldId);
		if (!relation) continue;

		const translated = idRelationQueryToRelationQuery(query, ctx);

		// The thumbnail virtual relation is the file itself, so fetch its fields on the parent.
		if (isFileThumbnailField(relation)) {
			result = mergeFieldSelections(result, translated);
		}
		else {
			result.include ??= {};
			result.include[relation.apiName] = translated;
		}
	}

	return result;
}

export function idRelationQueryToRelationQuery(query: IdRelationQuery, ctx: IdToFieldSelectionContext): RelationQuery {
	const { fields, include, sort, ...options } = query;
	const result: RelationQuery = { ...options, ...idFieldSelectionToFieldSelection({ fields, include }, ctx) };

	if (sort) {
		result.sort = [];

		for (const entry of sort) {
			for (const [id, sortOptions] of Object.entries(entry)) {
				const apiName = ctx.findPrimitiveFieldById(id as GlobalPrimitiveFieldId)?.apiName;

				if (apiName && sortOptions) {
					result.sort!.push({ [apiName]: sortOptions });
				}
			}
		}
	}

	return result;
}
